import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTRPC } from '../trpc'
import { Panel, Loading, ErrorNote, useTooltip, ViewTitle } from '../ui'

const COLORS = ['var(--s1)', 'var(--s2)', 'var(--s3)']
const W = 760
const H = 320
const ML = 44
const MR = 92
const MT = 14
const MB = 34

export function Retention() {
  const trpc = useTRPC()
  const q = useQuery(trpc.analytics.retention.queryOptions())
  const svgRef = useRef<SVGSVGElement>(null)
  const [crossDay, setCrossDay] = useState<number | null>(null)
  const { node: tipNode, setTip } = useTooltip()

  if (q.error) return <ErrorNote error={q.error} />
  if (!q.data) return <Loading what="retention" />

  const { maxDay, series } = q.data
  const x = (d: number) => ML + (d / maxDay) * (W - ML - MR)
  const y = (v: number) => MT + (1 - v) * (H - MT - MB)

  // Place end labels so they can never overlap, whatever the data does.
  const ends = series
    .map((s, i) => {
      const lastIndex = s.points.reduce<number>((acc, v, idx) => (v == null ? acc : idx), 0)
      const value = s.points[lastIndex]
      return value == null ? null : { s, i, lastIndex, value, cy: y(value), ly: 0 }
    })
    .filter((e): e is NonNullable<typeof e> => e !== null)
    .sort((a, b) => a.cy - b.cy)
  ends.forEach((e, i) => {
    e.ly = i === 0 ? e.cy : Math.max(e.cy, ends[i - 1]!.ly + 32)
  })

  const onMove = (ev: React.MouseEvent<SVGRectElement>) => {
    const svg = svgRef.current
    if (!svg) return
    const r = svg.getBoundingClientRect()
    const sx = ((ev.clientX - r.left) / r.width) * W
    const d = Math.max(0, Math.min(maxDay, Math.round(((sx - ML) / (W - ML - MR)) * maxDay)))
    setCrossDay(d)
    setTip({
      x: ev.clientX,
      y: ev.clientY,
      content: (
        <>
          <b>Day {d}</b>
          {series.map((s, i) => (
            <div className="r" key={s.key}>
              <span>
                <i className="swatch" style={{ display: 'inline-block', background: COLORS[i], verticalAlign: 'middle', marginRight: 6 }} />
                {s.label}
              </span>
              <span>{s.points[d] == null ? '—' : `${Math.round(s.points[d]! * 100)}%`}</span>
            </div>
          ))}
        </>
      ),
    })
  }

  return (
    <>
      <ViewTitle suit="diamond">When patients stop</ViewTitle>
      <p className="view-sub">
        Share of buyers still training on day N after purchase, counting only patients who have had the
        chance to reach that day. The shape tells you where to spend a message; the final number
        tells you who to sell to.
      </p>

      <Panel>
        <div className="legend">
          {series.map((s, i) => (
            <span key={s.key}>
              <i className="swatch" style={{ background: COLORS[i] }} />
              {s.label}
            </span>
          ))}
        </div>

        <div className="chart-wrap">
          <svg
            ref={svgRef}
            className="chart"
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="Retention curves by acquisition channel"
          >
            {[0, 0.25, 0.5, 0.75, 1].map((t) => (
              <g key={t}>
                <line x1={ML} x2={W - MR} y1={y(t)} y2={y(t)} stroke="var(--grid)" strokeWidth={1} />
                <text x={ML - 9} y={y(t) + 4} textAnchor="end" fontSize={11} fill="var(--ink-3)">
                  {t * 100}%
                </text>
              </g>
            ))}
            {[0, 10, 20, 30, 40, 50, 60]
              .filter((t) => t <= maxDay)
              .map((t) => (
                <text key={t} x={x(t)} y={H - MB + 20} textAnchor="middle" fontSize={11} fill="var(--ink-3)">
                  {t}
                </text>
              ))}
            <line x1={ML} x2={W - MR} y1={y(0)} y2={y(0)} stroke="var(--rule)" strokeWidth={1} />
            <text x={(ML + W - MR) / 2} y={H - 2} textAnchor="middle" fontSize={11} fill="var(--ink-3)">
              Days since purchase
            </text>

            {series.map((s, i) => (
              <path
                key={s.key}
                d={pathFor(s.points, x, y)}
                fill="none"
                stroke={COLORS[i]}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ))}

            {ends.map((e) => (
              <g key={e.s.key}>
                <circle cx={x(e.lastIndex)} cy={e.cy} r={4.5} fill={COLORS[e.i]} stroke="var(--surface)" strokeWidth={2} />
                <path d={`M${x(e.lastIndex) + 6} ${e.cy} L${x(e.lastIndex) + 13} ${e.ly - 4}`} fill="none" stroke={COLORS[e.i]} strokeWidth={1} opacity={0.45} />
                <text x={x(e.lastIndex) + 16} y={e.ly - 1} fontSize={12} fontWeight={600} fill={COLORS[e.i]}>
                  {Math.round(e.value * 100)}%
                </text>
                <text x={x(e.lastIndex) + 16} y={e.ly + 13} fontSize={11} fill="var(--ink-3)">
                  {e.s.label}
                </text>
              </g>
            ))}

            {crossDay != null && (
              <line x1={x(crossDay)} x2={x(crossDay)} y1={MT} y2={y(0)} stroke="var(--rule)" strokeWidth={1} />
            )}
            <rect
              x={ML}
              y={MT}
              width={W - ML - MR}
              height={H - MT - MB}
              fill="transparent"
              onMouseMove={onMove}
              onMouseLeave={() => {
                setCrossDay(null)
                setTip(null)
              }}
            />
          </svg>
        </div>

        <p className="footnote">
          The 30-session protocol takes most patients 6–8 weeks, so day 45 is the number that predicts
          a finished course. A day is left blank where fewer than {q.data.minDenominator} patients have
          reached it — too few to report.
        </p>
      </Panel>
      {tipNode}
    </>
  )
}

function pathFor(points: Array<number | null>, x: (d: number) => number, y: (v: number) => number) {
  let d = ''
  let begun = false
  points.forEach((v, i) => {
    if (v == null) return
    d += `${begun ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)} `
    begun = true
  })
  return d
}
